import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Crown, XCircle, Calendar, ExternalLink } from 'lucide-react';
import {
  cancelMySubscription, fetchMySubscriptions, formatMoney, friendlyError,
} from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { EmptyState } from '../EmptyState';
import { STATUS_BADGE_COLORS, STATUS_LABEL } from '../AccessBadge';

export function MySubscriptionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await fetchMySubscriptions();
      setItems(data.items || []);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const onCancel = async (sub) => {
    if (!window.confirm('¿Cancelar al final del periodo en curso? Mantendrás el acceso hasta esa fecha.')) return;
    setBusyId(sub.id);
    try {
      await cancelMySubscription(sub.id);
      toast.success('Cancelación programada.');
      await reload();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <SectionTitle
        icon={Crown}
        label="Mis suscripciones"
        hint="Planes activos e histórico."
      />
      {loading ? (
        <div className="space-y-2">{[1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Crown}
          title="Sin suscripciones activas"
          description="Con Premium accedes a más contenidos y partidos."
          ctaLabel="Ver planes"
          ctaTo="/planes"
          testId={DASHBOARD.subscriptionsEmpty}
        />
      ) : (
        <ul className="space-y-3">
          {items.map((s) => {
            const isCancellable = s.status === 'active' && !s.cancel_at_period_end;
            return (
              <li
                key={s.id}
                data-testid={DASHBOARD.subscriptionRow(s.id)}
                className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-4 rounded-xl border border-[var(--ns-border)]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-base font-semibold text-[var(--ns-text)]">{s.plan_name}</p>
                    <Badge className={`text-[10px] uppercase ${STATUS_BADGE_COLORS[s.status] || ''}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </Badge>
                    {s.cancel_at_period_end ? (
                      <Badge className="text-[10px] uppercase bg-amber-100 text-amber-800">Cancela al final</Badge>
                    ) : null}
                  </div>
                  <p className="text-xs text-[var(--ns-text-muted)] mt-1 flex items-center gap-1 flex-wrap">
                    <Calendar className="h-3 w-3" />
                    Vigente hasta: {s.ends_at ? new Date(s.ends_at).toLocaleDateString('es-ES') : 'sin límite'}
                    {s.plan?.price_cents ? (
                      <>
                        <span className="mx-1">·</span>
                        {formatMoney(s.plan.price_cents, s.plan.currency)} / mes
                      </>
                    ) : null}
                  </p>
                  {s.club ? (
                    <Button asChild variant="ghost" size="sm" className="mt-1 h-7 px-2 text-xs text-[var(--ns-gold-700)] hover:bg-[var(--ns-amber-50)]">
                      <Link to={`/clubes/${s.club.slug}`} className="inline-flex items-center gap-1">
                        Club: {s.club.name}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </Button>
                  ) : null}
                </div>
                {isCancellable ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyId === s.id}
                    onClick={() => onCancel(s)}
                    data-testid={DASHBOARD.subscriptionCancel(s.id)}
                    className="h-9 px-3 rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8 hover:border-[var(--ns-red)]"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Cancelar
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
